import router from "@/router";
import type { Channel } from "@/composables/useUtils";
import { useAlertStore } from "@/stores/alert";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";
import {
  type ApiResponse,
  type AttachTagDataResult,
  type Brand,
  type BusinessHours,
  type CardInfoPayload,
  type ChecklistResponse,
  type Client,
  type GetInsuranceData,
  type InProgressInsurance,
  type JourneyAccessEventPayload,
  type ListBikeResponse,
  type PagarmePayload,
  type PagarmeResponse,
  type PaymentMethodChoice,
  type PixOrderResult,
  type PixPaymentStatus,
  type Plan,
  type ReportModelNotFoundPayload,
  type SendRatingPayload,
  type SerialNumberRules,
  type SetClientPayload,
  type TagEntryResolution,
  type ViaCepResponse,
} from "@/types/api";
import { showToast } from "@/utils/toast";
import axios, { AxiosError, type AxiosPromise } from "axios";
import _ from "lodash";
import { journeySessionParam } from "./useJourneyAccessTracking";
import { useUtils } from "./useUtils";

const baseHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const headers = {
  ...baseHeaders,
  "X-API-KEY": import.meta.env.VITE_APP_PARTNER_API_KEY,
};
const multipartHeaders = {
  Accept: "application/json",
  "Content-Type": "multipart/form-data",
};

const util = useUtils();

const COMPULSORY_DEADLINE_EXPIRED_CODE = "insurance.compulsory.deadline.expired";

type InsuranceClientData = {
  id: number;
  name: string;
  phone: string;
  email: string;
  cpf?: string | null;
  birthDate?: string | null;
  rg?: string | null;
  cnh?: string | null;
  rne?: string | null;
};

type CompulsoryDeadlineExpiredErrorData = {
  code?: string;
  detail?: string;
};

function toClient(source: InsuranceClientData): Client {
  return {
    id: String(source.id),
    name: source.name,
    phone: source.phone,
    email: source.email,
    cpfName: null,
    cpf: source.cpf ?? null,
    birthDate: source.birthDate ?? null,
    rg: source.rg ?? null,
    cnh: source.cnh ?? null,
    rne: source.rne ?? null,
    createdAt: "",
    updatedAt: "",
    type: null,
  };
}

async function handleCompulsoryDeadlineExpired(error: AxiosError): Promise<boolean> {
  const data = error.response?.data as CompulsoryDeadlineExpiredErrorData | undefined;
  if (data?.code !== COMPULSORY_DEADLINE_EXPIRED_CODE) {
    return false;
  }

  const api = useApiStore();
  const store = useAppStore();
  const insuranceCode = api.insuranceCode;

  let resolvedClient = api.client;
  let resolvedClientId = api.clientId;
  const hasClientContact = Boolean(api.client?.email && api.client?.phone);
  if (!hasClientContact && insuranceCode) {
    try {
      const clientData = (await useApiClient().getInsuranceClient(insuranceCode)).data;
      resolvedClient = toClient(clientData);
      resolvedClientId = clientData.id;
    } catch {}
  }

  localStorage.removeItem("insuranceCode");
  store.insuranceCode = null;
  api.$reset();
  api.client = resolvedClient;
  api.clientId = resolvedClientId;

  useAlertStore().setAlert(data.detail ?? "");
  router.replace({ path: "/client-verification" });
  return true;
}

const pagarmeClient = axios.create({
  baseURL: import.meta.env.VITE_APP_PAGARME_API_ENDPOINT,
  headers: baseHeaders,
});

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_ENDPOINT,
  headers,
});

const viaCepClient = axios.create({
  baseURL: import.meta.env.VITE_APP_VIA_CEP_ENDPOINT,
});

pagarmeClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const store = useAppStore();
    const insuranceCode = store.insuranceCode;

    util.noticeError(error, {
      type: "pagarmeClient",
      details: "Pagar.me client error interceptor",
      data: error.response,
      insuranceCode,
    });

    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (await handleCompulsoryDeadlineExpired(error)) {
      return Promise.reject(error);
    }

    let message = "Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.";
    let generic = true;
    if (error.response?.data && (error.response.data as any).detail) {
      message = (error.response.data as any).detail;
      generic = false;
    }

    const isSilent = (error.config as any)?.silent === true;
    if (!isSilent) {
      showToast.warning({
        title: "Erro",
        description: message,
      });
    }

    const store = useAppStore();
    const insuranceCode = store.insuranceCode;

    util.noticeError(error, {
      type: "services",
      details: "Services error interceptor",
      message,
      insuranceCode,
    });

    return Promise.reject(error);
  },
);

//TODO melhorar essa tipagem
export const useApiClient = () => {
  return {
    initInsurance(store?: string, tag?: string, sellerContactId?: string | number) {
      return request<string>(
        apiClient.get(`insurance/new`, {
          params: {
            tag,
            storeCode: store,
            isFreePlan: !!tag,
            ...(sellerContactId !== undefined && { seller: sellerContactId }),
            ...journeySessionParam(),
          },
        }),
      );
    },
    resolveTagEntry(storeCode?: string, tag?: string, sellerContactId?: string | number) {
      return request<TagEntryResolution>(
        apiClient.post(`/insurance/resolve-tag-entry`, null, {
          params: {
            storeCode,
            tag,
            ...(sellerContactId !== undefined && { seller: sellerContactId }),
          },
        }),
      );
    },
    getData(insuranceCode: string) {
      return request<GetInsuranceData>(apiClient.get(`insurance/${insuranceCode}/get-data`));
    },
    listMultibrandBrands(scooter?: boolean) {
      return request<Brand[]>(apiClient.get(`brand/multibrand`, { params: { scooter } }));
    },
    setInsuranceBrand(insuranceCode: string, brandId: number) {
      return request<null>(apiClient.post(`insurance/${insuranceCode}/brand/${brandId}`));
    },
    attachTagData(insuranceCode: string, tag: string) {
      return request<AttachTagDataResult>(apiClient.post(`insurance/${insuranceCode}/tag/${tag}`));
    },
    checkEligibleBrandFreePlan(insuranceCode: string) {
      return request<boolean>(
        apiClient.get(`/insurance/${insuranceCode}/eligible-brand-free-plan`),
      );
    },
    listBikePlans(insuranceCode: string) {
      return request<Plan[]>(apiClient.get(`/insurance/${insuranceCode}/plan/list`));
    },
    async setPlans(insuranceCode: string, planIds: any[]) {
      return request<null>(apiClient.post(`insurance/${insuranceCode}/plans`, planIds));
    },
    setCPF(clientId: string, cpf: string, insuranceCode: string, birthDate: string) {
      const cpfNumbers = cpf.replace(/\D/g, "");
      return request<boolean>(
        apiClient.put(`insurance/${insuranceCode}/client/${clientId}/cpf/${cpfNumbers}`, {
          birthDate,
        }),
      );
    },
    checkClientCpf(cpf: string, danfe: string, insuranceCode: string) {
      return request<boolean>(
        apiClient.get(`insurance/${insuranceCode}/danfe/${danfe}/check-client-cpf/${cpf}`),
      );
    },
    acceptTerms(insuranceCode: string) {
      return request<null>(apiClient.put(`insurance/${insuranceCode}/terms/accept`));
    },
    setOwnerDafe(insuranceCode: string, ownerDanfeType: string) {
      return request<null>(
        apiClient.get(`insurance/${insuranceCode}/bike/owner-danfe/${ownerDanfeType}`),
      );
    },
    getCep(cep: string): AxiosPromise<ViaCepResponse> {
      return viaCepClient.get(`ws/${cep}/json`);
    },
    setAddress(insuranceCode: string, address: Record<string, any>) {
      return request<null>(apiClient.post(`insurance/${insuranceCode}/address`, address));
    },
    tokenCard(data: PagarmePayload) {
      return pagarmeClient.post<PagarmeResponse>(`tokens`, data, {
        params: {
          appId: import.meta.env.VITE_APP_PAGARME_PUBLIC_KEY,
        },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
    },
    setCardInfo(insuranceCode: string, cardInfo: CardInfoPayload) {
      return request<null>(apiClient.post(`insurance/${insuranceCode}/payment`, cardInfo));
    },
    setPaymentMethod(insuranceCode: string, method: PaymentMethodChoice, installments: number) {
      return request<null>(
        apiClient.post(`insurance/${insuranceCode}/payment-method`, {
          paymentMethod: method,
          installments,
        }),
      );
    },
    createPixPayment(insuranceCode: string) {
      return request<PixOrderResult>(apiClient.post(`insurance/${insuranceCode}/payment/pix`));
    },
    getPixPaymentStatus(insuranceCode: string) {
      return request<PixPaymentStatus>(
        apiClient.get(`insurance/${insuranceCode}/payment/pix/status`),
      );
    },
    checkIfBikeHasTag(serialNumber: string, brandSlug: string, insuranceCode: string) {
      return request<string>(
        apiClient.get(`insurance/${insuranceCode}/${serialNumber}/${brandSlug}`),
      );
    },
    getChecklist(insuranceCode: string) {
      return request<ChecklistResponse>(apiClient.get(`insurance/${insuranceCode}/checklist`));
    },
    getPlanByInsurance(insuranceCode: string) {
      return request<Plan>(apiClient.get(`insurance/${insuranceCode}/get-plan`));
    },
    setUser(data: SetClientPayload, insuranceCode: string) {
      return request<number>(apiClient.post(`insurance/${insuranceCode}/client`, data));
    },
    resendUserVerification(email: string, brand: string, insuranceCode: string) {
      return request<number>(
        apiClient.get(`insurance/${insuranceCode}/client/confirm-email/${email}?brand=${brand}`),
      );
    },
    getInsuranceClient(insuranceCode: string) {
      return request<InsuranceClientData>(apiClient.get(`insurance/${insuranceCode}/client`));
    },
    setBikeSerialNumber(insuranceCode: string, bikeSerialNumber: string) {
      return request<null>(
        apiClient.get(`insurance/${insuranceCode}/bike/serialnumber/${bikeSerialNumber}`),
      );
    },
    getBikeSerialRules(insuranceCode: string) {
      return request<SerialNumberRules | null>(
        apiClient.get(`insurance/${insuranceCode}/bike/serial-rules`),
      );
    },
    confirmClientForJourney(clientId: string, code: string, insuranceCode: string) {
      return request<Client>(
        apiClient.post(`/insurance/${insuranceCode}/client/${clientId}/code/${code}`),
      );
    },
    getInsuranceBike(insuranceCode: string) {
      return request<any>(apiClient.get(`/insurance/${insuranceCode}/bike`));
    },
    listBikes(insuranceCode: string) {
      return request<ListBikeResponse>(apiClient.get(`/insurance/${insuranceCode}/bikes`));
    },
    loadDanfe(insuranceCode: string, danfeCode: string) {
      const digits = _.replace(danfeCode, /\D/g, "");
      return request<any>(apiClient.post(`insurance/${insuranceCode}/danfe/${digits}`));
    },
    loadBrandData(insuranceCode?: string) {
      return request<{ id: number; name: string }>(apiClient.get(`/brand/context`));
    },
    getInsuranceBrandContext(insuranceCode: string) {
      return apiClient
        .get<{ id: number; name: string }>(`/insurance/${insuranceCode}/brand/context`)
        .then((res) => res.data);
    },
    getBusinessHours() {
      return request<BusinessHours>(apiClient.get(`/insurance/business-hours`));
    },
    setBike(insuranceCode: string, bikeId: number | string) {
      return request<GetInsuranceData>(apiClient.post(`insurance/${insuranceCode}/bike/${bikeId}`));
    },
    reportModelNotFound(insuranceCode: string, payload: ReportModelNotFoundPayload) {
      return request<GetInsuranceData>(
        apiClient.post(`insurance/${insuranceCode}/bike/report`, payload),
      );
    },
    setSelfie(insuranceCode: string, selfie: File | string) {
      const formData = new FormData();

      if (selfie instanceof File) {
        formData.append("selfie", selfie);
      } else {
        const base64Data = selfie.split(",")[1] || selfie;
        const mimeMatch = selfie.match(/data:([^;]+);/);
        const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const file = new File([blob], "selfie.jpg", { type: mimeType });
        formData.append("selfie", file);
      }

      return request<null>(
        apiClient.post(`insurance/${insuranceCode}/client/photo/selfie`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      );
    },
    sendBikeMedia(insuranceCode: string, type: string, file: File) {
      const formData = new FormData();
      formData.append("file", file);

      return request<null>(
        apiClient.post(`insurance/${insuranceCode}/bike/photo/${type}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      );
    },
    sendRating(data: SendRatingPayload) {
      return request<null>(apiClient.post(`/insurance/${data.insuranceCode}/rating`, data));
    },
    listInProgressJourneys(phone: string) {
      return request<InProgressInsurance[]>(
        apiClient.get(`/insurance/in-progress`, { params: { phone } }),
      );
    },
    createClient(name: string, email: string, phone: string) {
      return request<{ clientId: number }>(apiClient.post(`/client`, { name, email, phone }));
    },
    verifyClientCode(clientId: number, code: string) {
      return request<Client>(
        apiClient.post(`/client/${clientId}/verify-code`, null, { params: { code } }),
      );
    },
    startNewInsuranceForClient(
      clientId: number,
      storeCode?: string,
      tag?: string,
      sellerContactId?: string | number,
    ) {
      return request<string>(
        apiClient.get(`/insurance/new`, {
          params: {
            clientId,
            storeCode,
            tag,
            isFreePlan: !!tag,
            ...(sellerContactId !== undefined && { seller: sellerContactId }),
            ...journeySessionParam(),
          },
        }),
      );
    },
    ensureInsuranceForClient(
      clientId: number,
      storeCode?: string,
      tag?: string,
      sellerContactId?: string | number,
    ) {
      return request<string | null>(
        apiClient.post(`/insurance/ensure-for-client`, null, {
          params: {
            clientId,
            storeCode,
            tag,
            ...(sellerContactId !== undefined && { seller: sellerContactId }),
            ...journeySessionParam(),
          },
        }),
      );
    },
    // endpoints nao verificados
    listBikePlansBySku(bikeSku: string, brand: string) {
      return apiClient.get(`plan/list/bikeSku/${bikeSku}?brand=${brand}`);
    },
    sendConfirmationCode(insuranceCode: string) {
      return apiClient.get(`insurance/${insuranceCode}/send-confirmation-code`);
    },
    setDocument(
      insuranceCode: string,
      type: string,
      fileFront: File,
      fileBack: File,
      latitude: number,
      longitude: number,
    ) {
      return apiClient.post(
        `insurance/${insuranceCode}/client/photo/document/${type}`,
        toFormData({ front: fileFront, back: fileBack, latitude, longitude }),
        { headers: multipartHeaders },
      );
    },
    enableCampaign(insuranceCode: string) {
      return apiClient.post(`/insurance/${insuranceCode}/enable-campaign`);
    },
    disableCampaign(insuranceCode: string) {
      return apiClient.post(`/insurance/${insuranceCode}/disable-campaign`);
    },
    enableBrandFreePlan(insuranceCode: string) {
      return apiClient.post(`/insurance/${insuranceCode}/enable-brand-free-plan`);
    },
    finish(insuranceCode: string) {
      return request<boolean>(apiClient.post(`/insurance/${insuranceCode}/finish`));
    },
    deferInspection(insuranceCode: string) {
      return request<boolean>(apiClient.post(`/insurance/${insuranceCode}/defer-inspection`));
    },
    undoDeferInspection(insuranceCode: string) {
      return request<boolean>(apiClient.post(`/insurance/${insuranceCode}/undo-defer-inspection`));
    },
    sequenceScreenFlow(
      path: string,
      isNext: boolean,
      insuranceCode: string,
      clientId?: number,
      channel?: Channel,
    ): Promise<string> {
      return apiClient
        .get<string>(`/screen-flow`, {
          params: { path, isNext, insuranceCode, clientId, channel },
        })
        .then((res) => res.data);
    },

    createInsurance(
      brand: string,
      tag: string,
      user: any,
      planIds: string[],
      files: any,
      address: any,
      card?: any,
    ) {
      const formData = toFormData({
        planId: planIds.join(","),
        cpf: user.cpf,
        file: files,
        brand: brand,
        tag: tag,
        addressDto: JSON.stringify({
          zip: address.postalCode,
          street: address.street,
          number: address.number,
          complement: address.complement,
          district: address.neighborhood,
          city: address.city,
          state: address.state,
        }),
      });

      return apiClient.post("insurance", formData, {
        headers: multipartHeaders,
      });
    },
    getBrandParameters() {
      return apiClient.get(`/brand-parameter/context`);
    },
    getBrandTerms(insuranceCode: string) {
      return request<string>(apiClient.get(`/insurance/${insuranceCode}/terms`));
    },
    retrieveInsuranceProgress(insuranceCode: string, channel?: Channel) {
      return request<number>(
        apiClient.get(`/insurance/${insuranceCode}/progress`, { params: { channel } }),
      );
    },
    // metodos nao utilizados
    isBrandFreePlanEnabled(insuranceCode: string) {
      return apiClient.get(`/insurance/${insuranceCode}/brand-free-plan-enabled`);
    },
    logJourneyAccessEvent(payload: JourneyAccessEventPayload) {
      return apiClient.post(`/insurance/journey-access-events`, payload, {
        adapter: "fetch",
        fetchOptions: { keepalive: true },
        silent: true,
      } as any);
    },
  };

  function toFormData(obj: any, form?: FormData, namespace?: string): FormData {
    const fd = form || new FormData();
    let formKey;

    for (const property in obj) {
      util.log(`[FormData] Processing: ${property}`);

      if (Object.prototype.hasOwnProperty.call(obj, property) && obj[property]) {
        if (namespace) {
          formKey = namespace + "[" + property + "]";
        } else {
          formKey = property;
        }

        // if the property is an object, but not a File, use recursivity.
        if (obj[property] instanceof Date) {
          fd.append(formKey, obj[property].toISOString());
          util.log(`[FormData] Appended date: ${formKey} = ${obj[property].toISOString()}`);
        } else if (
          (typeof obj[property] === "object" || Array.isArray(obj[property])) &&
          !(obj[property] instanceof File)
        ) {
          toFormData(obj[property], fd, formKey);
        } else {
          fd.append(formKey, obj[property]);
          util.log(`[FormData] Appended: ${formKey} = ${obj[property]}`);
        }
      }
    }

    return fd;
  }

  function request<T>(promise: AxiosPromise<ApiResponse<T>>): Promise<ApiResponse<T>> {
    return promise.then((res) => res.data);
  }
};
